import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, DatePipe } from '@angular/common';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';
/* Material modules */
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';

import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';

import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';

import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { DEFAULT_LOCALE } from '@app/app.constants';
import { DirectivesModule } from '@core/directives/directives.module';
import {
  CustomCheckboxSelectComponent,
  CustomDatePickerComponent,
  CustomDateRangePickerComponent,
  CustomPaginatorComponent,
  CustomYearPickerComponent,
} from '@francetvpub/shared-tv-styles';
import { ChannelSelectWithRegListComponent } from '@shared/components/channel-select-with-reg-list/channel-select-with-reg-list.component';
import { ChannelSelectComponent } from '@shared/components/channel-select/channel-select.component';
import { ContractCommentComponent } from '@shared/contract/contract-item/contract-comment/contract-comment.component';
import { CriteriaBaseGroupComponent } from '@shared/contract/contract-item/contract-commitment/category-group-criteria-component/base/criteria-base-group.component';
import { CriteriaCommitmentDateGroupComponent } from '@shared/contract/contract-item/contract-commitment/category-group-criteria-component/commitment-date/criteria-commitment-date-group.component';
import { CriteriaGroupComponent } from '@shared/contract/contract-item/contract-commitment/category-group-criteria-component/criteria-group.component';
import { NatioNegoConditionDynamicComponent } from '@shared/contract/contract-item/contract-natio-nego/natio-nego-condition-dynamic/natio-nego-condition-dynamic.component';
import { NatioNegoManagingDialogComponent } from '@shared/contract/contract-item/contract-natio-nego/natio-nego-managing-dialog/natio-nego-managing-dialog.component';
import { ContractSellerSelectorComponent } from '@shared/contract/contract-seller-list/contract-seller-filters/contract-seller-selector/contract-seller-selector.component';
import { ContractSellerGridComponent } from '@shared/contract/contract-seller-list/contract-seller-grid/contract-seller-grid.component';
import { TvFileUploadPopupProgComponent } from '@shared/dialog/tv-file-upload-popup/tv-file-upload-popup.prog.component';
import { ChannelFilterPipe } from '@shared/pipes/channel-filter.pipe';
import { FileUploadInputComponent } from './components/file-upload-input/file-upload-input.component';
/* Stateless components */
import { MainHeaderComponent } from './components/headers/main-header/main-header.component';
import { HourFilterComponent } from './components/hour-filter/hour-filter.component';
import { InfoAlertComponent } from './components/info-alert/info-alert.component';
import { NavItemComponent } from './components/nav-item/nav-item.component';
import { SavePlanComponent } from './components/save-plan/save-plan.component';
import { CGRPOffManagingDialog } from './contract/contract-item/contract-cgrp-off/cgrp-off-managing-dialog/cgrp-off-managing-dialog.component';
import { CgrpOffOptionDialogComponent } from './contract/contract-item/contract-cgrp-off/cgrp-off-option-dialog/cgrp-off-option-dialog.component';
import { ContractCgrpOffComponent } from './contract/contract-item/contract-cgrp-off/contract-cgrp-off.component';
import { CGRPOnManagingDialog } from './contract/contract-item/contract-cgrp-on/cgrp-on-managing-dialog/cgrp-on-managing-dialog.component';
import { ContractCgrpOnComponent } from './contract/contract-item/contract-cgrp-on/contract-cgrp-on.component';
import { CommitmentGroupComponent } from './contract/contract-item/contract-commitment/category-group-commitment-component/commitment-group.component';
import { ContractCommitmentComponent } from './contract/contract-item/contract-commitment/contract-commitment.component';
import { ContractAdvertiserInformationsComponent } from './contract/contract-item/contract-general-informations/contract-advertiser-informations/contract-advertiser-informations.component';
import { ContractAdvertisingCenterGroupComponent } from './contract/contract-item/contract-general-informations/contract-advertising-center-group/contract-advertising-center-group.component';
import { ContractAdvertisingCenterInformationsComponent } from './contract/contract-item/contract-general-informations/contract-advertising-center-informations/contract-advertising-center-informations.component';
import { ContractGeneralInformationsComponent } from './contract/contract-item/contract-general-informations/contract-general-informations.component';
import { ContractInformationsComponent } from './contract/contract-item/contract-general-informations/contract-informations/contract-informations.component';
import { ContractManagerInformationsComponent } from './contract/contract-item/contract-general-informations/contract-manager-informations/contract-manager-informations.component';
import { ContractUtInformationsComponent } from './contract/contract-item/contract-general-informations/contract-ut-informations/contract-ut-informations.component';
import { ContractItemComponent } from './contract/contract-item/contract-item.component';
import { ContractNatioNegoComponent } from './contract/contract-item/contract-natio-nego/contract-natio-nego.component';
import { ContractThemaNegoComponent } from './contract/contract-item/contract-thema-nego/contract-thema-nego.component';
import { ThemaGuaranteedManagingDialogComponent } from './contract/contract-item/contract-thema-nego/thema-guaranteed-managing-dialog/thema-guaranteed-managing-dialog.component';
import { ThemaListPackDialogComponent } from './contract/contract-item/contract-thema-nego/thema-list-pack-dialog/thema-list-pack-dialog.component';
import { ThemaNotGuaranteedManagingDialogComponent } from './contract/contract-item/contract-thema-nego/thema-not-guaranteed-managing-dialog/thema-not-guaranteed-managing-dialog.component';
import { ThemaPackManagingDialogComponent } from './contract/contract-item/contract-thema-nego/thema-pack-managing-dialog/thema-pack-managing-dialog.component';
import { ContractCreatingDialogComponent } from './contract/contract-list/contract-creating-dialog/contract-creating-dialog.component';
import { ContractSellerFiltersComponent } from './contract/contract-seller-list/contract-seller-filters/contract-seller-filters.component';
import { ContractSellerListComponent } from './contract/contract-seller-list/contract-seller-list.component';
import { ContractComponent } from './contract/contract.component';
import { CommentAddDialogComponent } from './dialog/comment-add-dialog/comment-add-dialog.component';
import { CommentEditDialogComponent } from './dialog/comment-edit-dialog/comment-edit-dialog.component';
import { ConfirmDialogComponent } from './dialog/confirm-dialog/confirm-dialog.component';
import { FilterPopupComponent } from './dialog/filter-popup/filter-popup.component';
import { FiltersDialogComponent } from './dialog/filters-dialog/filters-dialog.component';
import { RegionDialogComponent } from './dialog/region-dialog/region-dialog.component';
import { ScreenDialogComponent } from './dialog/screen-dialog/screen-dialog.component';
import { ScreenHistoricComponent } from './dialog/screen-dialog/screen-historic/screen-historic.component';
import { ScreenInformationComponent } from './dialog/screen-dialog/screen-information/screen-information.component';
import { ScreenSpotsComponent } from './dialog/screen-dialog/screen-spots/screen-spots.component';
import { ScreenTypeComponent } from './dialog/screen-dialog/screen-type/screen-type.component';
import { TvConfirmPopupComponent } from './dialog/tv-confirm-popup/tv-confirm-popup.component';
import { TvFileUploadPopupComponent } from './dialog/tv-file-upload-popup/tv-file-upload-popup.component';
import { TvPlanValuationDialogComponent } from './dialog/tv-plan-valuation-dialog/tv-plan-valuation-dialog.component';
import { TvSelectDatePopupComponent } from './dialog/tv-select-date-popup/tv-select-date-popup.component';
import { TvSelectItemsDialogComponent } from './dialog/tv-select-items-dialog/tv-select-items-dialog.component';
import { TvSelectPackChannelsDialogComponent } from './dialog/tv-select-pack-channels-dialog/tv-select-pack-channels-dialog.component';
/* Pipes */
import { ContractListComponent } from '@shared/contract/contract-list/contract-list.component';
import { Moment } from 'moment';
import { NumberFormaterPipe } from './pipes/number-formater.pipe';

const MAT_FR_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

class TvMomentDateAdapter extends MomentDateAdapter {
  override format(date: Moment, displayFormat: string | Intl.DateTimeFormatOptions): string {
    if (typeof displayFormat === 'object') {
      return new Intl.DateTimeFormat(DEFAULT_LOCALE, displayFormat).format(date.toDate());
    }

    return super.format(date, displayFormat);
  }
}

@NgModule({
  declarations: [
    MainHeaderComponent,
    NavItemComponent,
    NumberFormaterPipe,
    SavePlanComponent,
    ConfirmDialogComponent,
    FileUploadInputComponent,
    ChannelFilterPipe,
    InfoAlertComponent,
    TvConfirmPopupComponent,
    FilterPopupComponent,
    HourFilterComponent,
    FiltersDialogComponent,
    TvSelectDatePopupComponent,
    ScreenDialogComponent,
    ScreenInformationComponent,
    ScreenTypeComponent,
    ScreenSpotsComponent,
    TvFileUploadPopupComponent,
    TvFileUploadPopupProgComponent,
    ContractComponent,
    ContractSellerListComponent,
    ContractItemComponent,
    ScreenHistoricComponent,
    ContractCgrpOffComponent,
    ContractCgrpOnComponent,
    ContractNatioNegoComponent,
    ContractCommitmentComponent,
    ContractCommentComponent,
    CGRPOffManagingDialog,
    CGRPOnManagingDialog,
    NatioNegoManagingDialogComponent,
    NatioNegoConditionDynamicComponent,
    CgrpOffOptionDialogComponent,
    TvSelectItemsDialogComponent,
    RegionDialogComponent,
    ContractCommentComponent,
    CommitmentGroupComponent,
    CriteriaGroupComponent,
    CriteriaBaseGroupComponent,
    CriteriaCommitmentDateGroupComponent,
    CommentEditDialogComponent,
    CommentAddDialogComponent,
    ChannelSelectComponent,
    ChannelSelectWithRegListComponent,
    ContractThemaNegoComponent,
    ThemaGuaranteedManagingDialogComponent,
    ThemaPackManagingDialogComponent,
    ThemaListPackDialogComponent,
    TvSelectPackChannelsDialogComponent,
    ContractSellerFiltersComponent,
    ContractSellerGridComponent,
    ContractSellerSelectorComponent,
    ThemaNotGuaranteedManagingDialogComponent,
    ContractGeneralInformationsComponent,
    ContractAdvertiserInformationsComponent,
    ContractManagerInformationsComponent,
    ContractUtInformationsComponent,
    ContractAdvertisingCenterInformationsComponent,
    ContractInformationsComponent,
    ContractAdvertisingCenterGroupComponent,
    ContractCreatingDialogComponent,
    ContractListComponent,
    TvPlanValuationDialogComponent,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    DirectivesModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatExpansionModule,
    MatTableModule,
    MatStepperModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatToolbarModule,
    MatIconModule,
    MatTabsModule,
    MatCardModule,
    MatButtonModule,
    MatTooltipModule,
    MatSidenavModule,
    MatListModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatGridListModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatMomentDateModule,
    MatPaginatorModule,
    MatSelectModule,
    MatDatepickerModule,
    MatSortModule,
    MatChipsModule,
    MatCheckboxModule,
    MatRadioModule,
    CustomCheckboxSelectComponent,
    CustomDatePickerComponent,
    CustomDateRangePickerComponent,
    CustomPaginatorComponent,
    CustomYearPickerComponent,
  ],
  exports: [
    // Modules
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatExpansionModule,
    MatTableModule,
    MatStepperModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatToolbarModule,
    MatIconModule,
    MatTabsModule,
    MatCardModule,
    MatButtonModule,
    MatTooltipModule,
    MatSidenavModule,
    MatListModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatGridListModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatMomentDateModule,
    MatPaginatorModule,
    MatDatepickerModule,
    MatSortModule,
    MatChipsModule,
    MatCheckboxModule,
    DragDropModule,
    // Components
    MainHeaderComponent,
    NavItemComponent,
    ConfirmDialogComponent,
    FileUploadInputComponent,
    TvConfirmPopupComponent,
    CustomDateRangePickerComponent,
    HourFilterComponent,
    // Pipes
    NumberFormaterPipe,
    ChannelFilterPipe,
    InfoAlertComponent,
    FilterPopupComponent,
    CustomCheckboxSelectComponent,
    CustomPaginatorComponent,
    CGRPOffManagingDialog,
    CGRPOnManagingDialog,
    CgrpOffOptionDialogComponent,
    RegionDialogComponent,
    TvFileUploadPopupComponent,
    TvFileUploadPopupProgComponent,
    ChannelSelectComponent,
    ChannelSelectWithRegListComponent,
    ContractListComponent,
    CustomDatePickerComponent,
    CustomYearPickerComponent,
    TvPlanValuationDialogComponent,
  ],
  providers: [
    DatePipe,
    { provide: LOCALE_ID, useValue: DEFAULT_LOCALE },
    { provide: MAT_DATE_LOCALE, useValue: DEFAULT_LOCALE },
    { provide: MAT_DATE_FORMATS, useValue: MAT_FR_DATE_FORMATS },
    {
      provide: DateAdapter,
      useClass: TvMomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
})
export class SharedModule {}
